export type ComparisonPercentDecimals = 0 | 1;

export const COMPARISON_NEAR_ZERO_THRESHOLD = 1e-9;
export const COMPARISON_NEW_LABEL = 'New';
export const COMPARISON_NO_DATA_LABEL = 'No data';

export type ComparisonDisplayKind = 'new' | 'no-data' | 'equal' | 'change';

export interface ComparisonDisplayState {
	kind: ComparisonDisplayKind;
	displayCurrent: number;
	displayPrevious: number;
	percentChange: number | null;
}

function isMissing(value: number | null | undefined): boolean {
	return value == null || !Number.isFinite(value);
}

function isEmptyOrZero(value: number | null | undefined): boolean {
	if (isMissing(value)) {
		return true;
	}
	return Math.abs(value) < COMPARISON_NEAR_ZERO_THRESHOLD;
}

/** Classify current vs previous for New / No data / equal / change. */
export function resolveComparisonDisplayState(
	current: number | null | undefined,
	previous: number | null | undefined
): ComparisonDisplayState {
	const currentEmpty = isEmptyOrZero(current);
	const previousEmpty = isEmptyOrZero(previous);

	if (currentEmpty && previousEmpty) {
		return {
			kind: 'no-data',
			displayCurrent: 0,
			displayPrevious: 0,
			percentChange: null,
		};
	}

	if (!currentEmpty && previousEmpty) {
		return {
			kind: 'new',
			displayCurrent: current as number,
			displayPrevious: 0,
			percentChange: null,
		};
	}

	if (currentEmpty && !previousEmpty) {
		return {
			kind: 'change',
			displayCurrent: 0,
			displayPrevious: previous as number,
			percentChange: -100,
		};
	}

	const currentValue = current as number;
	const previousValue = previous as number;
	if (
		Math.abs(currentValue - previousValue) < COMPARISON_NEAR_ZERO_THRESHOLD
	) {
		return {
			kind: 'equal',
			displayCurrent: currentValue,
			displayPrevious: previousValue,
			percentChange: 0,
		};
	}

	return {
		kind: 'change',
		displayCurrent: currentValue,
		displayPrevious: previousValue,
		percentChange:
			((currentValue - previousValue) / Math.abs(previousValue)) * 100,
	};
}

/** Percent inside parentheses, e.g. `(-28.0%)` or `(+8%)`. */
export function formatComparisonPercentInParens(
	percentChange: number,
	decimals: ComparisonPercentDecimals
): string {
	if (percentChange === 0) {
		return decimals === 1 ? '(0.0%)' : '(0%)';
	}
	const sign = percentChange > 0 ? '+' : '-';
	return `(${sign}${Math.abs(percentChange).toFixed(decimals)}%)`;
}

export function resolveComparisonPolarityColor(
	percentChange: number,
	inverseMetric?: boolean
): string {
	if (percentChange === 0) {
		return 'var(--text-secondary-color)';
	}
	const isPositive = inverseMetric ? percentChange <= 0 : percentChange >= 0;
	return isPositive
		? 'var(--status-success-color)'
		: 'var(--status-danger-color)';
}

/** Derive previous-period absolute value from current and % change. */
export function derivePreviousFromPercentChange(
	current: number,
	percentChange: number
): number | null {
	if (!Number.isFinite(current) || !Number.isFinite(percentChange)) {
		return null;
	}
	const divisor = 1 + percentChange / 100;
	if (Math.abs(divisor) < COMPARISON_NEAR_ZERO_THRESHOLD) {
		return null;
	}
	return current / divisor;
}

export interface PreviousWithPercentHtmlParams {
	previousFormatted: string;
	percentChange: number;
	decimals: ComparisonPercentDecimals;
	inverseMetric?: boolean;
	fontFamilyStyle?: string;
}

/** `{previous} ({percent})` with polarity-colored previous and percent. */
export function buildPreviousWithPercentHtml(
	params: PreviousWithPercentHtmlParams
): string {
	const pctText = formatComparisonPercentInParens(
		params.percentChange,
		params.decimals
	);
	const color = resolveComparisonPolarityColor(
		params.percentChange,
		params.inverseMetric
	);
	const label = `${params.previousFormatted} ${pctText}`;
	const fontFamilyStyle = params.fontFamilyStyle ?? '';

	return `<span style="color:${color};font-size:var(--font-size-xs);font-weight:var(--font-weight-medium);white-space:nowrap;${fontFamilyStyle}">${label}</span>`;
}

export function buildComparisonStatusHtml(
	kind: 'new' | 'no-data',
	options?: { fontFamilyStyle?: string }
): string {
	const zeroLabel = `0 (${kind === 'new' ? COMPARISON_NEW_LABEL : COMPARISON_NO_DATA_LABEL})`;
	const fontFamilyStyle = options?.fontFamilyStyle ?? '';
	return `<span style="color:var(--text-secondary-color);font-size:var(--font-size-xs);font-weight:var(--font-weight-medium);white-space:nowrap;${fontFamilyStyle}">${zeroLabel}</span>`;
}
